/**
 * Menú «Emojis» del texto de la plantilla (pedido de Alan del 26/10/2026: «que me deje tocar algo de emojis que me traiga
 * todos los emojis que existen así puedo elegir»): todos los de Unicode por grupo, con un buscador en español («corazón»,
 * «fuego», sin importar las tildes). Los datos se bajan al abrirlo por primera vez (`logic/emojis-datos.ts`).
 *
 * Queda abierto para elegir varios seguidos. Con el tope del validador (10) se apaga: con más, no se manda a aprobar.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown, Loader2, Smile } from "lucide-react";
import { buscar, MAX_EMOJIS, type GrupoEmojis } from "../logic/emojis";
import { cargarEmojis } from "../logic/emojis-datos";

interface Props {
  /** los emojis que ya tiene el mensaje, contados como el validador (sobre el cuerpo compuesto) */
  emojis: number;
  onElegir: (emoji: string) => void;
  disabled?: boolean;
}

export function SelectorEmojis({ emojis, onElegir, disabled = false }: Props) {
  const [abierto, setAbierto] = useState(false);
  const [grupos, setGrupos] = useState<GrupoEmojis[] | null>(null);
  const [error, setError] = useState(false);
  /** sube con «Probar de nuevo»: vuelve a pedir los datos */
  const [intento, setIntento] = useState(0);
  const [grupo, setGrupo] = useState(0);
  const [texto, setTexto] = useState("");
  const contRef = useRef<HTMLDivElement>(null);
  const idMenu = useId();
  const lleno = emojis >= MAX_EMOJIS;

  useEffect(() => {
    if (!abierto || grupos) return;
    let vivo = true;
    setError(false);
    cargarEmojis().then((g) => vivo && setGrupos(g), () => vivo && setError(true));
    return () => { vivo = false; };
  }, [abierto, grupos, intento]);

  // Cerrar con un click afuera o con Escape, como «Insertar variable»: un menú abierto tapa el texto que se edita.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => { if (contRef.current && !contRef.current.contains(e.target as Node)) setAbierto(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", esc);
    };
  }, [abierto]);

  const visibles = useMemo(() => {
    if (!grupos) return [];
    return texto.trim() ? buscar(grupos, texto) : grupos[grupo]?.emojis || [];
  }, [grupos, grupo, texto]);

  return (
    <div ref={contRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={abierto}
        aria-controls={abierto ? idMenu : undefined}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
      >
        <Smile size={13} /> Emojis <ChevronDown size={12} />
      </button>

      {abierto && (
        <div id={idMenu} role="dialog" aria-label="Elegir un emoji"
          className="absolute left-0 z-30 mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white shadow-lg">
          {error ? (
            <div className="p-3 text-sm text-slate-600">
              No pudimos traer los emojis.{" "}
              <button type="button" onClick={() => setIntento((n) => n + 1)} className="font-semibold text-violet-700 hover:underline">
                Probar de nuevo
              </button>
            </div>
          ) : !grupos ? (
            <p className="p-3 text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Cargando emojis…</p>
          ) : (
            <>
              <div className="p-2 border-b border-slate-100">
                <input
                  type="search"
                  autoFocus
                  aria-label="Buscar un emoji"
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="Buscar: corazón, fuego, regalo…"
                  className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              {!texto.trim() && (
                <div role="tablist" aria-label="Grupos de emojis" className="flex gap-0.5 px-1.5 pt-1.5 overflow-x-auto scrollbar-hide">
                  {grupos.map((g, i) => (
                    <button key={g.numero} type="button" role="tab" aria-selected={i === grupo} title={g.nombre} aria-label={g.nombre}
                      onClick={() => setGrupo(i)}
                      className={`flex-shrink-0 rounded-md px-1.5 py-1 text-lg leading-none ${i === grupo ? "bg-violet-100" : "hover:bg-slate-100"}`}>
                      {g.emojis[0]?.unicode}
                    </button>
                  ))}
                </div>
              )}
              <div className="h-56 overflow-y-auto p-1.5">
                {visibles.length ? (
                  <div className="grid grid-cols-8 gap-0.5">
                    {visibles.map((e) => (
                      <button key={e.hexcode} type="button" title={e.label} aria-label={e.label} disabled={lleno}
                        onClick={() => onElegir(e.unicode)}
                        className="rounded-md py-1 text-xl leading-none hover:bg-violet-50 disabled:opacity-40 disabled:hover:bg-transparent">
                        {e.unicode}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="p-2 text-sm text-slate-500">No hay emojis con «{texto.trim()}».</p>
                )}
              </div>
              <p className={`px-3 py-2 border-t border-slate-100 text-[11px] tabular-nums ${lleno ? "text-amber-700" : "text-slate-500"}`}>
                {lleno
                  ? `Ya tiene ${MAX_EMOJIS} emojis, el máximo para mandarla a aprobar.`
                  : `${emojis} de ${MAX_EMOJIS} emojis en el mensaje.`}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SelectorEmojis;
